import { Button, Stack, Typography, IconButton, Skeleton } from '@mui/material';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import { makeStyles } from '@mui/styles';
import ReactPlayer from 'react-player';
import { useCandidateProfileContext } from '../hooks/use-candidate-profile-context';
import { createCodingInterview, sendCodingInterviewEmail } from 'src/services/role.service';
import { fetchCodingInterviewResult } from 'src/services/interview.service';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';
import { INTERVIEW_CLIENT_URL } from 'src/config-global';
import { Editor } from '@monaco-editor/react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

const useStyles = makeStyles(theme => ({
  topContainerAnalysis: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    padding: '18px 15px',
    gap: 34,
    width: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 6,
  },
  bottomContainerAnalysis: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    padding: '18px 15px',
    gap: 34,
    width: '50%',
    borderRadius: 6,
  },
  codingReportDiv: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
    padding: 20,
    gap: 16,
  },
  scoreBtn: {
    color: theme.palette.content.accent1,
    background: theme.palette.background.primaryAccent2,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryAccent2,
    },
  },
  disabledScoreBtn: {
    color: theme.palette.content.primaryBlack3,
    background: theme.palette.background.primaryGrey2,
    fontWeight: 600,
  },
  recordedBtn: {
    color: theme.palette.content.primaryBlack1,
    background: theme.palette.background.primaryGrey1,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryGrey1,
    },
  },
}));

const scoreState = 'scoreState';
const videoState = 'videoState';
const codeState = 'codeState';

const CodingAssessment = () => {
  // hooks
  const {
    obj: candidateDetails,
    jobId,
    refetchWithoutLoading,
  } = useCandidateProfileContext();
  const { user } = useAuthContext();
  const orgId = getOrganizationId(user);
  const { copyToClipboard } = useCopyToClipboard();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const isCodingInterviewCreated = candidateDetails?.coding_interview;
  const interviewUrl = candidateDetails?.coding_interview?.interview_url;

  // states
  const [isLoading, setIsLoading] = useState(false);
  const [recordedDataState, setRecordedDataState] = useState(null);
  const [isInterviewLinkCopied, setIsInterviewLinkCopied] = useState(false);
  const [interviewDetails, setInterviewDetails] = useState(null);
  const [aiInterviewLoader, setAiInterviewLoader] = useState(true);
  const [interviewStatus, setInterviewStatus] = useState('UNATTEMPTED');

  // functions
  const createInterview = async () => {
    try {
      setIsLoading(true);

      const payload = {
        user_id: candidateDetails.candidate_info.user_id,
        interview_round: 0,
      };

      const response = await createCodingInterview(orgId, jobId, payload);

      if (response?.success) {
        enqueueSnackbar('Created coding interview');
      }
    } catch (err) {
      console.log('err: ', err);

      enqueueSnackbar('Failed to create coding interview', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
      refetchWithoutLoading();
    }
  };

// functions
  const sendInterviewInviteEmail = async () => {
    try {
      setIsLoading(true);

      const payload = {};

      const response = await sendCodingInterviewEmail(candidateDetails?.coding_interview?.coding_interview_id, payload);

      if (response?.success) {
        enqueueSnackbar('Sent coding interview invite');
      }
    } catch (err) {
      console.log('err: ', err);

      enqueueSnackbar('Failed to send coding interview email', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyInterviewLink = () => {
    copyToClipboard({
      text: INTERVIEW_CLIENT_URL + interviewUrl,
      onSuccess: () => {
        setIsInterviewLinkCopied(true);
        setTimeout(() => {
          setIsInterviewLinkCopied(false);
        }, 2000);
      },
    });
  };

  const getButtonText = () => {
    if (isCodingInterviewCreated) {
      if (isInterviewLinkCopied) {
        return 'Copied!';
      }
      return 'Copy';
    }
    return 'Create';
  };

  useEffect(() => {
    if (!candidateDetails?.coding_interview?.coding_interview_id) {
      setAiInterviewLoader(false);
      return;
    }
    setAiInterviewLoader(true);
    fetchCodingInterviewResult(
      candidateDetails?.coding_interview?.coding_interview_id,
    )
      .then(res => {
        if (res?.body?.interview_status !== 'UNATTEMPTED') {
          const _questions = res?.body?.coding_questions
            ? Object.keys(res?.body?.coding_questions).map(key => ({
                question_id: key,
                ...res?.body?.coding_questions[key],
              }))
            : null;

          const _solutions = res?.body?.coding_solutions
            ? Object.keys(res?.body?.coding_solutions).map(key => ({
                question_id: key,
                ...res?.body?.coding_solutions[key],
              }))
            : null;

          setInterviewDetails(res?.body);
        }
        setInterviewStatus(res?.body?.interview_status);
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setAiInterviewLoader(false);
      });
  }, [candidateDetails?.coding_interview?.coding_interview_id]);

  if (aiInterviewLoader)
    return (
      <Stack
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        Loading...
      </Stack>
    );

  return interviewStatus !== 'UNATTEMPTED' &&
    interviewDetails &&
    interviewDetails?.coding_solutions !== null ? (
    <Stack spacing="20px">
      {/* <Stack direction="row" spacing="20px">
        <Stack className={classes.topContainerAnalysis}>
          <Stack spacing="17px">
            <Stack spacing="10px" direction="row">
              <Typography variant="body1">Rank</Typography>
              <Image width="20px" src="/assets/circleI.svg" />
            </Stack>
            <Typography variant="h1">
              {interviewDetails?.rank || '_'}
            </Typography>
          </Stack>
        </Stack>
        <Stack className={classes.topContainerAnalysis}>
          <Stack spacing="17px">
            <Stack spacing="10px" direction="row">
              <Typography variant="body1">Score</Typography>
            </Stack>
            <Stack spacing="10px" direction="row">
              <Typography variant="h1">
                {interviewDetails?.score || '_'}
              </Typography>
              <Typography variant="body1">/30</Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack> */}
      <Stack direction="row" spacing="12px">
        <Stack className={classes.bottomContainerAnalysis}>
          <Stack spacing="17px">
            <Stack spacing="10px" direction="row" alignItems="center">
              <Iconify icon="teenyicons:user-square-outline" />
              <Typography variant="body1">
                Attempted on {interviewDetails?.attempted_at || '_'}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack className={classes.bottomContainerAnalysis}>
          <Stack spacing="17px">
            <Stack spacing="10px" direction="row" alignItems="center">
              <Iconify icon="octicon:clock-24" />
              <Typography variant="body1">
                Interview Duration:{' '}
                {(
                  interviewDetails?.coding_solutions['1']?.time_taken_seconds /
                  60
                ).toFixed(1) || '_'}{' '}
                min
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Stack className={classes.codingReportDiv}>
        <Typography variant="subtitle1">Coding Report</Typography>
        <Stack spacing="12px">
          {interviewDetails?.coding_solutions &&
            Object.keys(interviewDetails?.coding_solutions).map(
              (key, index) => {
                const item = interviewDetails?.coding_solutions[key];

                const isActiveScore =
                  item?.isEvaluated !== null &&
                  item?.solution?.isEvaluated !== undefined;
                const isActiveScreenRecording =
                  !!item?.screen_recording_url ||
                  !!(item?.screen_recordings && item?.screen_recordings.length);
                const isActiveCode = !!item?.solution_code;

                return (
                  <>
                    <Stack direction="row" spacing="12px">
                      <Stack key={index} spacing="12px" direction="row">
                        {/* <Button
                          variant="contained"
                          size="small"
                          disabled={!isActiveScore}
                          className={
                            isActiveScore
                              ? classes.scoreBtn
                              : classes.disabledScoreBtn
                          }
                        >
                          Score - {item?.score || 0}
                        </Button> */}
                        {(item?.screen_recording_url ||
                          (item?.screen_recordings &&
                            item?.screen_recordings.length)) && (
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!isActiveScreenRecording}
                            className={classes.recordedBtn}
                            startIcon={<Iconify icon="tabler:screen-share" />}
                            sx={{
                              border: theme =>
                                recordedDataState === videoState &&
                                `1px solid ${theme.palette.border.primaryAccent1}`,
                            }}
                            onClick={() => {
                              if (recordedDataState === videoState) {
                                setRecordedDataState(null);
                              } else {
                                setRecordedDataState(videoState);
                              }
                            }}
                          >
                            Screen Recording
                          </Button>
                        )}
                        {item?.solution_code && (
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!isActiveCode}
                            className={classes.recordedBtn}
                            startIcon={<Iconify icon="ph:code" />}
                            sx={{
                              border: theme =>
                                recordedDataState === codeState &&
                                `1px solid ${theme.palette.border.primaryAccent1}`,
                            }}
                            onClick={() => {
                              if (recordedDataState === codeState) {
                                setRecordedDataState(null);
                              } else {
                                setRecordedDataState(codeState);
                              }
                            }}
                          >
                            Code
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                    <Stack
                      spacing="12px"
                      direction="row"
                      justifyContent="center"
                    >
                      {recordedDataState === videoState &&
                        (item?.screen_recording_url ? (
                          <Stack position="relative">
                            <ReactPlayer
                              width="100%"
                              height="400px"
                              controls
                              url={item?.screen_recording_url}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                right: '12px',
                                top: '12px',
                                bgcolor: theme =>
                                  theme.palette.background.primaryGrey2,
                                color: theme =>
                                  theme.palette.content.primaryBlack1,
                              }}
                              onClick={() => {
                                setRecordedDataState(null);
                              }}
                            >
                              <Iconify icon="tabler:x" />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Carousel showArrows showThumbs={false}>
                            {item?.screen_recordings.map(
                              (recording, _index) => (
                                <Stack position="relative" pb="30px" sx={{
                                  paddingX: '34px',
                                }} bgcolor="#000000" key={_index}>
                                  <ReactPlayer
                                    width="100%"
                                    height="400px"
                                    controls
                                    url={recording}
                                  />
                                  <IconButton
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      right: '36px',
                                      top: '12px',
                                      bgcolor: theme =>
                                        theme.palette.background.primaryGrey2,
                                      color: theme =>
                                        theme.palette.content.primaryBlack1,
                                    }}
                                    onClick={() => {
                                      setRecordedDataState(null);
                                    }}
                                  >
                                    <Iconify icon="tabler:x" />
                                  </IconButton>
                                </Stack>
                              ),
                            )}
                          </Carousel>
                        ))}
                      {recordedDataState === codeState && (
                        <Stack position="relative">
                          <Editor
                            width="720px"
                            height="400px"
                            language={item?.language}
                            value={item?.solution_code}
                            theme={'vs-dark'}
                            className="code-editor"
                            sx={{
                              borderRadius: '30px',
                            }}
                            options={{ readOnly: true }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              right: '12px',
                              top: '12px',
                              bgcolor: theme =>
                                theme.palette.background.primaryGrey2,
                              color: theme =>
                                theme.palette.content.primaryBlack1,
                            }}
                            onClick={() => {
                              setRecordedDataState(null);
                            }}
                          >
                            <Iconify icon="tabler:x" />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>
                  </>
                );
              },
            )}
        </Stack>
      </Stack>
    </Stack>
  ) : (
    <Stack
      height="100%"
      width="100%"
      alignItems="center"
      justifyContent="center"
    >
      <Stack alignItems="center" justifyContent="center" spacing="20px">
        <Iconify icon="tabler:mail-share" width={64} />
        <Typography fontSize={18}>
          {isCodingInterviewCreated ? 'Copy' : 'Create'} coding assessment link
        </Typography>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          startIcon={<Iconify icon="tabler:code" />}
          sx={{
            width: '220px',
            bgcolor: isInterviewLinkCopied
              ? 'background.primaryGreen2'
              : 'background.primaryBlack1',
            color: isInterviewLinkCopied
              ? 'content.primaryGreen1'
              : 'content.primaryWhite',

            '&:hover': {
              color: isInterviewLinkCopied
                ? 'background.primaryGreen2'
                : 'content.primaryWhite',
              bgcolor: isInterviewLinkCopied
                ? 'content.primaryGreen1'
                : 'background.primaryAccent1',
            },
          }}
          onClick={() => {
            if (!isCodingInterviewCreated) {
              createInterview();
            } else if (interviewUrl) {
              handleCopyInterviewLink();
            }
          }}
        >
          {getButtonText()}
        </LoadingButton>
      { isCodingInterviewCreated && <LoadingButton
          startIcon={<Iconify icon="tabler:mail" />}
          variant="outlined"
          sx={{
            width: '220px'
          }}
          onClick={() => {
              sendInterviewInviteEmail();
          }}
        >
          Resend Link
        </LoadingButton>
      }
      </Stack>
    </Stack>
  );
};

export default CodingAssessment;
